<template>
   <div class="toc" data-lenis-prevent>
      <div
         v-for="(t, i) in toc"
         :class="['toc-cat', { 'active-cat': t.id?.includes(active.cat) }]"
         :key="`toc-${i}`"
      >
         <Btn
            v-if="t.id"
            :class="['toc-title']"
            :text="t.title"
            textLink
            action
            @handle-action="handleScroll(t.id, 1)"
         />
         <p v-else class="toc-title">{{ t.title }}</p>
         <div class="toc-cat-anchors">
            <Btn
               v-for="a in t.anchors"
               :class="[
                  'toc-anchor',
                  { 'active-anchor': a.id == `${active.anchor}` },
               ]"
               :text="a.label"
               textLink
               action
               @handle-action="handleScroll(a.id, 1)"
               :key="a.id"
            />
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
const { scrollState } = useLenis();
const { toc } = useTOC();
// >> REFS -----------------------------------
const active = reactive({
   cat: null,
   anchor: null,
   allAnchorIds: [],
   ignoreObserver: false,
});
let anchorObserver = null;

// >> METHODS -----------------------------------
const handleScroll = async (id, duration = 1) => {
   // handle anchor selection
   // prevent observer overriding user choice
   active.ignoreObserver = true;
   // active.anchor = id.substring(1);
   active.anchor = id;
   const scrolled = await scrollToEl(
      `#${id}`,
      duration,
      window.innerHeight * -0.07 - 24
   );
   scrolled && (active.ignoreObserver = false);
};

const getObserverTargets = (val) =>
   new Promise((resolve) => {
      // iterate on toc sections
      let anchorObjs = [];
      for (const [key, value] of Object.entries(val)) {
         anchorObjs = [...anchorObjs, value];
         active.allAnchorIds = [...active.allAnchorIds, value.id];
         value.anchors.forEach((anchor) => {
            // gather all anchor obj
            anchorObjs = [...anchorObjs, anchor];
            // gather all ids in order for observer scrolling
            active.allAnchorIds = [
               ...active.allAnchorIds,
               // anchor.id.substring(1),
               anchor.id,
            ];
         });
      }
      resolve(anchorObjs);
   });

const handleActiveCat = (anchor) => {
   // check which toc section has an active anchor
   let foundKey = null;
   for (const [key, value] of Object.entries(toc.value)) {
      anchor.includes(`${key}`) && (foundKey = key);
   }
   active.cat = foundKey;
};

const watchAnchor = (entries) => {
   if (!active.ignoreObserver && entries[0].isIntersecting) {
      // when scrolling down the page slot in active
      active.anchor = entries[0].target.id;
   } else if (!active.ignoreObserver && scrollState.value.direction < 0) {
      // when scrolling up the page slot in prev anchor
      const getPrevId = active.allAnchorIds?.indexOf(entries[0].target.id);
      active.anchor = active.allAnchorIds[getPrevId - 1];
   }
};

// >> WATCHERS/OBSERVERS -----------------------------------
watch(
   toc,
   async (val) => {
      if (!document) return;
      await nextTick();
      const anchorObjs = await getObserverTargets(val);
      anchorObserver = new IntersectionObserver(watchAnchor, {
         root: document,
         threshold: 0.01,
         rootMargin: "0px 0px -60% 0px",
      });

      anchorObjs.forEach((anchor) => {
         if (document.querySelector(`#${anchor.id}`)) {
            anchorObserver.observe(document.querySelector(`#${anchor.id}`));
         }
         active.anchor = active.allAnchorIds[0];
      });
   },
   { deep: true }
);

watch(
   () => active.anchor,
   (inc) => {
      inc && handleActiveCat(inc);
   }
);
</script>

<style lang="scss" scoped>
@import "~/assets/scss/globals/toc";
</style>
